import { useSelectable } from '../hooks/useSelectable'
import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { formatDateAndTime } from '../../../utils/stringUtils'
import { ExternallinkIcon } from '@ordr-as/icons'
import { NonCapturedPaymentDto } from '../../../../generated-api'
import { usePaymentOperations, WatchedRowKind, WatchedRowStatus } from '../context/PaymentOperationsContext'
import { Button } from '@ordr-as/umami-react'
import { RefreshButton } from '../RefreshButton'
import CollapsiblePanel from '../CollapsiblePanel'
import Badge from '../components/Badge'

export const NonCapturedPaymentsTable = ({ nonCapturedPayments }: { nonCapturedPayments: NonCapturedPaymentDto[] }) => {
    const { retryNonCaptured, isRefreshing, watchedRows } = usePaymentOperations()

    const canRetry = (payment: NonCapturedPaymentDto) => {
        const retryStates = ['AUTHORISED', 'CAPTURING']
        const retryProviders = ['ADYEN', 'SOFTPAY']
        return !!(
            retryProviders.includes(payment.paymentProvider) &&
            payment.latestState &&
            retryStates.includes(payment.latestState)
        )
    }

    const { selected, isChecked, toggle, isAllChecked, toggleAll, isNonChecked, reset } = useSelectable(
        nonCapturedPayments,
        {
            getId: (notification) => notification.paymentId,
            isSelectable: canRetry,
        }
    )

    const retryAll = useCallback(() => {
        const paymentIds = selected
        retryNonCaptured(paymentIds)
        reset()
    }, [reset, retryNonCaptured, selected])

    const retrySingleItem = useCallback(
        (payment: NonCapturedPaymentDto) => {
            const paymentIds = [payment.paymentId]
            retryNonCaptured(paymentIds)
            toggle(payment)
        },
        [retryNonCaptured, toggle]
    )

    const getRowStatusClass = (id: number) => {
        const status = watchedRows.find((row) => row.id === id && row.kind === WatchedRowKind.payment)?.status

        const defaultClass = 'hover:bg-purple-lightest border-t border-grey-lightest cursor-pointer'
        if (!status) {
            return defaultClass
        }

        return status === WatchedRowStatus.processing
            ? defaultClass + ' bg-brand-accent'
            : defaultClass + ' bg-green-lightest'
    }

    const retryButtonText = isAllChecked ? 'Retry all' : 'Retry selected'

    return (
        <CollapsiblePanel title={`Non-Captured Payments (${nonCapturedPayments?.length})`} defaultOpen>
            <div>
                <div className="flex justify-end gap-4 px-5 pb-2">
                    <Button isDisabled={isNonChecked || isRefreshing} onClick={retryAll} kind="secondary">
                        {retryButtonText}
                    </Button>
                    <RefreshButton />
                </div>
                <div className="overflow-x-auto">
                    <table className="text-left min-w-full">
                        <thead className="text-grey-dark">
                            <tr>
                                <th className="py-2 px-5">
                                    <input type="checkbox" checked={isAllChecked} onChange={toggleAll} />
                                </th>
                                <th className="font-semibold py-2 px-2">Reference</th>
                                <th className="font-semibold py-2 px-2">PaymentId</th>
                                <th className="font-semibold py-2 px-2"> Status</th>
                                <th className="font-semibold py-2 px-2">Events</th>
                                <th className="font-semibold py-2 px-2">Payment Method</th>
                                <th className="font-semibold py-2 px-2">Provider</th>
                                <th className="font-semibold py-2 px-2">Location ID</th>
                                <th className="font-semibold py-2 px-2">Created</th>
                                <th className="font-semibold py-2 px-2">Updated</th>
                                <th className="font-semibold py-2 px-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {nonCapturedPayments.length ? (
                                nonCapturedPayments.map((payment) => (
                                    <tr
                                        key={payment.paymentId}
                                        className={getRowStatusClass(payment.paymentId)}
                                        onClick={() => canRetry(payment) && toggle(payment)}
                                    >
                                        <td className="py-2 px-2">
                                            <input
                                                type="checkbox"
                                                disabled={!canRetry(payment)}
                                                checked={isChecked(payment)}
                                                onChange={() => toggle(payment)}
                                            />
                                        </td>
                                        <td className="py-2 px-2 text-sm">
                                            <Link
                                                to={'/payments/' + payment.paymentReference}
                                                className="flex gap-1 text-brand-primary "
                                                target="_blank"
                                            >
                                                {payment.paymentReference}{' '}
                                                <ExternallinkIcon className="text-xs inline align-baseline" />
                                            </Link>
                                        </td>
                                        <td>{payment.paymentId}</td>
                                        <td className="py-2 px-2 text-sm">
                                            <Badge label={payment.latestState?.toString() ?? ''} />
                                        </td>
                                        <td className="py-2 px-2 text-sm text-center">{payment.events.length}</td>
                                        <td className="py-2 px-2 text-sm">
                                            <Badge label={payment.paymentMethod} />
                                        </td>
                                        <td className="py-2 px-2 text-sm">{payment.paymentProvider}</td>
                                        <td className="py-2 px-2 text-sm text-center">{payment.executingLocationId}</td>
                                        <td className="py-2 px-2 text-sm">{formatDateAndTime(payment.created)}</td>
                                        <td className="py-2 px-2 text-sm">{formatDateAndTime(payment.updated)}</td>
                                        <td className="py-2 px-2 text-sm flex">
                                            <Link
                                                to={`${payment.paymentReference}`}
                                                className="text-brand-primary mr-2"
                                            >
                                                Details
                                            </Link>
                                            {canRetry(payment) && (
                                                <button
                                                    onClick={() => retrySingleItem(payment)}
                                                    className="text-brand-primary focus:outline-none"
                                                    type="button"
                                                >
                                                    Retry
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="py-4 px-5 text-center">
                                        No non-captured payments
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </CollapsiblePanel>
    )
}
